import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Search, Package, ShieldAlert, Bike, AlertTriangle, Loader2, ChevronRight, KeyRound,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/statuses";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const KPIS = [
  { key: "active", label: "Active Shipments", icon: Package, color: "text-sky-600" },
  { key: "customs_holds", label: "Customs Holds", icon: ShieldAlert, color: "text-amber-600" },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Bike, color: "text-indigo-600" },
  { key: "exceptions", label: "Exceptions", icon: AlertTriangle, color: "text-red-600" },
];

const EMPTY = {
  description: "", service_level: "Standard", origin_city: "", origin_country: "",
  dest_city: "", dest_country: "", estimated_delivery: "", recipient_name: "",
  recipient_phone: "", recipient_address: "", internal_notes: "", tracking_number: "",
};

export default function Dashboard() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", newp: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (statusFilter !== "all") p.status = statusFilter;
    if (kindFilter !== "all") p.kind = kindFilter;
    const [s, list] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/shipments", { params: p }),
    ]);
    setStats(s.data);
    setRows(list.data);
    setLoading(false);
  }, [q, statusFilter, kindFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      const { data } = await api.post("/shipments", payload);
      toast.success(`Shipment created — ${data.tracking_number}`);
      setOpen(false);
      setForm(EMPTY);
      nav(`/dashboard/shipments/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.newp !== pw.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (pw.newp.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.newp,
        confirm_password: pw.confirm,
      });
      setPw({ current: "", newp: "", confirm: "" });
      setPwOpen(false);
      toast.success("Password changed. Please sign in with your new password.");
      await logout();
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Operations Dashboard</h1>
            <p className="text-sm text-slate-500">Create and manage your shipments.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            data-testid="change-password-btn"
            onClick={() => setPwOpen(true)}
            className="gap-1.5"
          >
            <KeyRound className="h-4 w-4" /> Change password
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-shipment-btn" className="gap-1.5 bg-orange-600 hover:bg-orange-700">
                <Plus className="h-4 w-4" /> New Shipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Create shipment</DialogTitle>
                <DialogDescription>Public route info is shown to customers; private fields stay staff-only.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} data-testid="create-shipment-form" className="space-y-3">
                <div>
                  <Label>Package description *</Label>
                  <Input required data-testid="cs-description" value={form.description} onChange={set("description")} className="mt-1" placeholder="Electronics — 1 parcel, 2.4 kg" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Service level</Label>
                    <Input data-testid="cs-service" value={form.service_level} onChange={set("service_level")} className="mt-1" />
                  </div>
                  <div>
                    <Label>Est. delivery</Label>
                    <Input type="date" data-testid="cs-eta" value={form.estimated_delivery} onChange={set("estimated_delivery")} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Origin city *</Label>
                    <Input required data-testid="cs-origin-city" value={form.origin_city} onChange={set("origin_city")} className="mt-1" />
                  </div>
                  <div>
                    <Label>Origin country *</Label>
                    <Input required data-testid="cs-origin-country" value={form.origin_country} onChange={set("origin_country")} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Destination city *</Label>
                    <Input required data-testid="cs-dest-city" value={form.dest_city} onChange={set("dest_city")} className="mt-1" />
                  </div>
                  <div>
                    <Label>Destination country *</Label>
                    <Input required data-testid="cs-dest-country" value={form.dest_country} onChange={set("dest_country")} className="mt-1" />
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Private (never shown publicly)</p>
                  <div className="space-y-2">
                    <Input data-testid="cs-recipient-name" value={form.recipient_name} onChange={set("recipient_name")} placeholder="Recipient name" />
                    <Input data-testid="cs-recipient-phone" value={form.recipient_phone} onChange={set("recipient_phone")} placeholder="Recipient phone" />
                    <Input data-testid="cs-recipient-address" value={form.recipient_address} onChange={set("recipient_address")} placeholder="Recipient full address" />
                    <Input data-testid="cs-internal-notes" value={form.internal_notes} onChange={set("internal_notes")} placeholder="Internal notes" />
                  </div>
                </div>
                <div>
                  <Label>Tracking number (optional — auto-generated if blank)</Label>
                  <Input data-testid="cs-tracking" value={form.tracking_number} onChange={set("tracking_number")} className="mt-1 font-mono" placeholder="Leave blank to auto-generate" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="cs-submit" className="bg-slate-900 hover:bg-slate-800">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create shipment"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Change password</DialogTitle>
                <DialogDescription>
                  Enter your current password, then choose a new one. You'll be signed out of all sessions.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={changePassword} data-testid="change-password-form" className="space-y-3">
                <div>
                  <Label>Current password</Label>
                  <Input
                    type="password"
                    required
                    data-testid="cp-current"
                    value={pw.current}
                    onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>New password</Label>
                  <Input
                    type="password"
                    required
                    data-testid="cp-new"
                    value={pw.newp}
                    onChange={(e) => setPw((p) => ({ ...p, newp: e.target.value }))}
                    className="mt-1"
                    placeholder="At least 8 characters"
                  />
                </div>
                <div>
                  <Label>Confirm new password</Label>
                  <Input
                    type="password"
                    required
                    data-testid="cp-confirm"
                    value={pw.confirm}
                    onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={pwSaving} data-testid="cp-submit" className="bg-slate-900 hover:bg-slate-800">
                    {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <Card key={k.key} data-testid={`kpi-${k.key}`} className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{k.label}</p>
                  <Icon className={`h-4 w-4 ${k.color}`} />
                </div>
                <p className="font-heading mt-2 text-3xl font-bold text-slate-900">
                  {stats ? stats[k.key] : "—"}
                </p>
              </Card>
            );
          })}
        </div>

        <Card className="mt-6 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="shipment-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search tracking #, description, city..."
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="sm:w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="held_by_customs">Held by Customs</SelectItem>
                <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="exception">Exception</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger data-testid="kind-filter" className="sm:w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="live">Live only</SelectItem>
                <SelectItem value="demo">Demo only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-sm text-slate-500" data-testid="shipments-empty">No shipments match your filters.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="shipments-table">
              {rows.map((r) => (
                <motion.button
                  key={r.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid={`shipment-row-${r.tracking_number}`}
                  onClick={() => nav(`/dashboard/shipments/${r.id}`)}
                  className="flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-slate-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-slate-900">{r.tracking_number}</span>
                      {r.is_demo && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Demo</span>}
                    </div>
                    <p className="truncate text-sm text-slate-500">
                      {r.description} · {r.origin_city} → {r.dest_city}
                    </p>
                  </div>
                  <div className="hidden sm:block"><StatusBadge status={r.status} size="sm" /></div>
                  <span className="hidden font-mono text-xs text-slate-400 md:block">{fmtDateTime(r.updated_at)}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                </motion.button>
              ))}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
